import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { dashboard } from '@/api/endpoints';
import { hasPermission, type DashboardData } from '@/types';
import { Icon } from '@/components/Icon';
import { useAuth } from '@/auth/AuthContext';
import { useDashboardLayout } from './dashboard/useDashboardLayout';
import { DashboardGrid } from './dashboard/DashboardGrid';
import { WIDGET_MAP, widgetAllowedForPerms } from './dashboard/registry';

/**
 * Dashboard = powłoka: pobiera dane (`/api/dashboard`), trzyma układ widgetów per użytkownik
 * (`useDashboardLayout`) i renderuje je przez `DashboardGrid`. Tryb „Dostosuj" pozwala ukrywać,
 * przestawiać (drag&drop) i zmieniać szerokość kafelków; „Zapisz" utrwala układ na koncie.
 */
export function Dashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const { layout, setLayout, save, revert, resetDefault, dirty, loading, saving } = useDashboardLayout();
  const { user } = useAuth();
  // Gating widgetów idzie po UPRAWNIENIACH (macierz), nie po nazwach ról —
  // role są dynamiczne i tworzone w UI.
  const perms = user?.permissions;

  useEffect(() => {
    dashboard.get().then(setData).catch(e => setErr(String(e)));
  }, []);

  if (err) return <div className="text-red-500">Błąd: {err}</div>;
  if (!data || loading) return <div className="text-ink-400">Ładowanie…</div>;

  const hidden = layout.filter(it => !it.visible && widgetAllowedForPerms(it.key, perms));
  const maJakikolwiekWidget = layout.some(it => widgetAllowedForPerms(it.key, perms));
  const restore = (key: string) => setLayout(layout.map(x => x.key === key ? { ...x, visible: true } : x));

  const onSave = async () => { await save(); setEditing(false); };
  const onCancel = () => { revert(); setEditing(false); };

  return (
    <div className="max-w-[1400px]">
      <header className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1>Witaj w Hydro-Wolski</h1>
          {/* Licznik terminów dotyczy OPRACOWAŃ — bez STUDY_VIEW pokazywałby zawsze zero. */}
          <div className="text-ink-400 text-[13px]">
            Dziś: {data.today}
            {hasPermission(user, 'STUDY_VIEW') && ` · w tym tygodniu ${data.deadlinesThisWeek.length} terminów`}
          </div>
        </div>
        <div className="flex gap-2">
          {editing ? (
            <>
              <button onClick={resetDefault} className="btn btn-ghost btn-sm">Przywróć domyślne</button>
              <button onClick={onCancel} className="btn btn-secondary btn-sm">Anuluj</button>
              <button onClick={onSave} disabled={saving || !dirty} className="btn btn-primary btn-sm">
                <Icon name="check" className="w-3.5 h-3.5"/> {saving ? 'Zapisywanie…' : 'Zapisz'}
              </button>
            </>
          ) : (
            <>
              {/* Nie ma czego dostosowywać, gdy uprawnienia nie obejmują żadnego widgetu. */}
              {maJakikolwiekWidget && (
                <button onClick={() => setEditing(true)} className="btn btn-secondary btn-sm">
                  <Icon name="settings" className="w-3.5 h-3.5"/> Dostosuj
                </button>
              )}
              {/* Trasa /intake-wizard i tak wymaga INTAKE_CREATE — bez tego warunku przycisk
                  prowadził użytkownika bez uprawnienia prosto w ekran „brak dostępu"
                  (pasek górny w Layout.tsx gate'uje ten sam przycisk od dawna). */}
              {hasPermission(user, 'INTAKE_CREATE') && (
                <NavLink to="/intake-wizard" className="btn btn-primary btn-sm">
                  <Icon name="plus" className="w-3.5 h-3.5"/> Nowe ujęcie
                </NavLink>
              )}
            </>
          )}
        </div>
      </header>

      {editing && (
        <div className="mb-4 p-3 rounded-md border border-water-200 bg-water-50/50 text-[12px] text-ink-600">
          <div className="flex items-center gap-1.5">
            <Icon name="edit" className="w-3.5 h-3.5 text-water-600"/>
            Tryb edycji: przeciągnij kafelki (albo użyj strzałek « »), aby zmienić kolejność; ikonami zmień szerokość (¼ / ½ / 1/1) lub ukryj widget.
          </div>
          {hidden.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5 items-center">
              <span className="text-ink-500">Ukryte widgety:</span>
              {hidden.map(it => (
                <button key={it.key} onClick={() => restore(it.key)}
                        className="pill hover:bg-water-100 flex items-center gap-1" style={{ background: '#eef2ff' }}>
                  <Icon name="plus" className="w-3 h-3"/> {WIDGET_MAP[it.key].title}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <DashboardGrid data={data} editing={editing} layout={layout} setLayout={setLayout} />
    </div>
  );
}
